import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import AdvancedRemediationNav from "./AdvancedRemediation/AdvancedRemediationNav";
import { runtimeConfig } from "src/services/runtimeConfig";
import styles from "./AdHocDialer.module.css";

const ADHOC_DIALER_API_URL = `${runtimeConfig.dataApiUrl}/v1.4`;

interface DialerFormData {
  callerId: string;
  destinationType: "custom" | "preset";
  customDestination: string;
  presetDestination: string;
  durationType: "preset" | "custom";
  callDuration: string;
  customDurationSeconds: string;
}

interface PresetDestination {
  label: string;
  number: string;
}

const PRESET_DESTINATIONS: PresetDestination[] = [
  { label: "Ronnie Phone 1", number: "7077999400" },
  { label: "Ronnie Phone 2", number: "9714648899" },
  { label: "Jeff", number: "5039367187" },
];

const CALL_DURATION_OPTIONS = [
  { label: "30 seconds", value: "30s" },
  { label: "2 minutes", value: "2m" },
  { label: "4 minutes", value: "4m" },
  { label: "5 minutes", value: "5m" },
];

// Format phone number as XXX-XXX-XXXX while typing (+1 shown as input group addon)
const formatPhoneInput = (value: string): string => {
  const cleanValue = value.replace(/^\+1\s*/, "");
  let digits = cleanValue.replace(/\D/g, "");
  if (digits.startsWith("1") && digits.length > 1) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Format raw 10-digit number for display as +1 XXX-XXX-XXXX
const formatPhoneDisplay = (digits: string): string => {
  if (digits.length !== 10) return digits;
  return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Extract 10 digits from formatted phone (strips +1 or 1 prefix)
const extractDigits = (phone: string): string => {
  // First get all digits
  let digits = phone.replace(/\D/g, "");
  // If we have 11 digits starting with 1, it's the country code - strip it
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
};

// Convert formatted phone to API format (1XXXXXXXXXX)
const formatPhoneForApi = (phone: string): string => {
  const digits = extractDigits(phone);
  return `1${digits}`;
};

/**
 * Ad Hoc Dialer page for sysadmins to originate test calls
 * with customer caller IDs via the Asterisk API.
 *
 * Route: /sysadmin/ad-hoc-dialer
 */
const AdHocDialer: React.FC = () => {
  const { phone: phoneParam } = useParams<{ phone?: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DialerFormData>(() => ({
    callerId: phoneParam ? formatPhoneInput(phoneParam) : "",
    destinationType: "preset",
    customDestination: "",
    presetDestination: PRESET_DESTINATIONS[0].number,
    durationType: "preset",
    callDuration: "30s",
    customDurationSeconds: "60",
  }));

  // Keep the URL path in sync with the caller ID so links are shareable.
  useEffect(() => {
    const digits = extractDigits(formData.callerId);
    const target =
      digits.length === 10
        ? `/sysadmin/ad-hoc-dialer/${digits}`
        : `/sysadmin/ad-hoc-dialer`;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [formData.callerId, navigate]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: "callerId" | "customDestination",
  ) => {
    const formatted = formatPhoneInput(e.target.value);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: formatted,
    }));
  };

  const handleDestinationTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      destinationType: e.target.value as "custom" | "preset",
    }));
  };

  const handleDurationTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      durationType: e.target.value as "preset" | "custom",
    }));
  };

  const getDestinationNumber = (): string => {
    if (formData.destinationType === "custom") {
      return formData.customDestination;
    }
    return formatPhoneDisplay(formData.presetDestination);
  };

  const isValidPhoneNumber = (phone: string): boolean => {
    const digits = extractDigits(phone);
    return digits.length === 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    // Validate caller ID
    if (!isValidPhoneNumber(formData.callerId)) {
      setError("Please enter a valid 10-digit caller ID");
      setIsLoading(false);
      return;
    }

    // Get and validate destination
    const destinationDisplay = getDestinationNumber();
    if (
      formData.destinationType === "custom" &&
      !isValidPhoneNumber(formData.customDestination)
    ) {
      setError("Please enter a valid 10-digit destination number");
      setIsLoading(false);
      return;
    }

    const destinationForApi =
      formData.destinationType === "custom"
        ? formatPhoneForApi(formData.customDestination)
        : `1${formData.presetDestination}`;

    const callerIdForApi = formatPhoneForApi(formData.callerId);

    try {
      let response;
      let durationLabel: string;

      if (formData.durationType === "preset") {
        // Use preset duration with context parameter
        const queryParams = new URLSearchParams({
          numberToDial: destinationForApi,
          callerId: callerIdForApi,
          context: formData.callDuration,
        }).toString();

        response = await callApiWithOptions({
          endpoint: `AdhocDialer?${queryParams}`,
          method: HttpMethod.GET,
          baseUrl: ADHOC_DIALER_API_URL,
          credentials: "omit", // API uses AllowAnyOrigin which doesn't support credentials
        });
        durationLabel =
          CALL_DURATION_OPTIONS.find((o) => o.value === formData.callDuration)
            ?.label || formData.callDuration;
      } else {
        // Use custom duration in seconds
        const durationSeconds = parseInt(formData.customDurationSeconds, 10);
        if (isNaN(durationSeconds) || durationSeconds < 1) {
          setError("Please enter a valid duration in seconds (minimum 1)");
          setIsLoading(false);
          return;
        }

        const queryParams = new URLSearchParams({
          numberToDial: destinationForApi,
          callerId: callerIdForApi,
          durationSeconds: durationSeconds.toString(),
        }).toString();

        response = await callApiWithOptions({
          endpoint: `AdhocDialer?${queryParams}`,
          method: HttpMethod.GET,
          baseUrl: ADHOC_DIALER_API_URL,
          credentials: "omit", // API uses AllowAnyOrigin which doesn't support credentials
        });
        durationLabel = `${durationSeconds} seconds`;
      }

      setSuccessMessage(
        `Call initiated successfully!\n\nCaller ID: ${formatPhoneInput(formData.callerId)}\nDestination: ${destinationDisplay}\nDuration: ${durationLabel}`,
      );

      console.log("Asterisk response:", response);
    } catch (err: any) {
      let errorMessage = "Failed to initiate call";

      if (err.data) {
        const errorData = err.data;
        errorMessage = errorData.message || errorMessage;
        if (errorData.error) {
          errorMessage += `\n\nError Details: ${errorData.error}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <AdvancedRemediationNav phoneNumber={extractDigits(formData.callerId)} />

      {error && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <pre
            className="mb-0"
            style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}
          >
            {error}
          </pre>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" role="alert">
          <h4 className="alert-heading">Success</h4>
          <pre
            className="mb-0"
            style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}
          >
            {successMessage}
          </pre>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Caller ID */}
            <div className="row mb-3">
              <label className={`col-sm-2 col-form-label ${styles.formLabel}`}>
                <span className="text-danger">*</span> Caller ID:
              </label>
              <div className="col-sm-4">
                <div className="input-group">
                  <span className="input-group-text">+1</span>
                  <input
                    type="tel"
                    className="form-control"
                    id="callerId"
                    name="callerId"
                    value={formData.callerId}
                    onChange={(e) => handlePhoneInputChange(e, "callerId")}
                    placeholder="XXX-XXX-XXXX"
                    required
                  />
                </div>
                <small className="text-muted">
                  Phone number to display as caller ID
                </small>
              </div>
            </div>

            {/* Destination */}
            <div className="row mb-3">
              <label className={`col-sm-2 col-form-label`}>
                <span className="text-danger">*</span> Destination:
              </label>
              <div className="col-sm-10">
                <div className="d-flex align-items-center gap-4">
                  {/* Preset option */}
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="radio"
                      name="destinationType"
                      id="destinationPreset"
                      value="preset"
                      checked={formData.destinationType === "preset"}
                      onChange={handleDestinationTypeChange}
                    />
                    <select
                      className="form-select"
                      id="presetDestination"
                      name="presetDestination"
                      value={formData.presetDestination}
                      onChange={handleInputChange}
                      disabled={formData.destinationType !== "preset"}
                    >
                      {PRESET_DESTINATIONS.map((dest) => (
                        <option key={dest.number} value={dest.number}>
                          {dest.label} ({formatPhoneDisplay(dest.number)})
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Custom option */}
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="radio"
                      name="destinationType"
                      id="destinationCustom"
                      value="custom"
                      checked={formData.destinationType === "custom"}
                      onChange={handleDestinationTypeChange}
                    />
                    <div className="input-group">
                      <span className="input-group-text">+1</span>
                      <input
                        type="tel"
                        className="form-control"
                        id="customDestination"
                        name="customDestination"
                        value={formData.customDestination}
                        onChange={(e) =>
                          handlePhoneInputChange(e, "customDestination")
                        }
                        placeholder="XXX-XXX-XXXX"
                        disabled={formData.destinationType !== "custom"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call Duration */}
            <div className="row mb-3">
              <label className={`col-sm-2 col-form-label`}>
                <span className="text-danger">*</span> Call Duration:
              </label>
              <div className="col-sm-10">
                <div className="d-flex align-items-center gap-4">
                  {/* Preset option */}
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="radio"
                      name="durationType"
                      id="durationPreset"
                      value="preset"
                      checked={formData.durationType === "preset"}
                      onChange={handleDurationTypeChange}
                    />
                    <select
                      className="form-select"
                      id="callDuration"
                      name="callDuration"
                      value={formData.callDuration}
                      onChange={handleInputChange}
                      disabled={formData.durationType !== "preset"}
                    >
                      {CALL_DURATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Custom option */}
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="radio"
                      name="durationType"
                      id="durationCustom"
                      value="custom"
                      checked={formData.durationType === "custom"}
                      onChange={handleDurationTypeChange}
                    />
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        id="customDurationSeconds"
                        name="customDurationSeconds"
                        value={formData.customDurationSeconds}
                        onChange={handleInputChange}
                        min="1"
                        max="3600"
                        disabled={formData.durationType !== "custom"}
                      />
                      <span className="input-group-text">sec</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="row mb-3">
              <div className="col-sm-10 offset-sm-2">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Initiating Call...
                    </>
                  ) : (
                    "Initiate Call"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdHocDialer;
